import React, { forwardRef, useState, useImperativeHandle, useMemo, useEffect } from "react";
import { arrayMove, RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { v4 } from "uuid";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { standardErrorMessages } from "src/utils/errors";
import { PermissionEnum } from "src/context/Auth/interface";
import Title from "../Title";
import SortableContainer from "../Sortable/Container";
import { IComponentFCA, IFCA, IInternalFCA, IRefComponentFCA } from "./interface";
import FCAItem from "./FCAItem";
import Loader from "../Loader";
import { restrictToVerticalAxis } from "../Sortable/Container/modifiers";
import CheckPermission from "../CheckPermission";

const FCA: React.ForwardRefRenderFunction<IRefComponentFCA, IComponentFCA> = ({
	id,
	id2,
	tablesRelated,
	disabled,
	isPresentation,
	defaultValue
}, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { confirm } = useConfirmModal();

	const [loading, setLoading] = useState(false); 
	const [fcas, setFcas] = useState<IInternalFCA[]>([]);

	const handleDragEnd = async (activeId: string, overId: string) => {
		const activeIndex = fcas.findIndex((checks) => checks.id === activeId);
		const overIndex = fcas.findIndex((checks) => checks.id === overId);

		const firstFcaToMove = fcas[activeIndex];
		const secondFcaToMove = fcas[overIndex];

		if(firstFcaToMove.ina002Id <= 0 || secondFcaToMove.ina002Id <= 0){
			addToast({
				title: translate("Erro"),
				description: translate("Você está tentando alterar a posição de uma FCA não salva"),
				color: "yellow"
			});
			return;
		}

		setFcas((prevState) => arrayMove(prevState, activeIndex, overIndex));

		try{
			await RCCServicesApi.putRequest({
				method: "FCA/UpdateOrder",
				body: {
					firstFCA: {
						ina002Id: firstFcaToMove.ina002Id,
						order: overIndex
					},
					secondFCA:{
						ina002Id: secondFcaToMove.ina002Id,
						order: activeIndex
					},
					tablesRelated,
					idRelated: id,
					id2Related: id2
				}
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao reordenar FCA"), addToast);
		}
	};

	const handleDeleteFCA = (idForDelete: number, indexForDelete: number) => {
		async function deleteMethod(){
			await RCCServicesApi.deleteRequest({
				method: "FCA/Delete",
				urlParameters: {
					id: idForDelete
				}
			});
		}
		confirm(
			translate("Exclusão de FCA"), 
			`${translate("As ações vinculadas a FCA também serão excluídas")}. ${translate("Deseja realmente excluir a FCA")}?`,
			async () => {
				if(idForDelete > 0){
					await deleteMethod();
					setFcas((prevState) => prevState.filter((fca) => fca.ina002Id !== idForDelete));
				}else{
					setFcas((prevState) => prevState.filter((_, ind) => ind !== indexForDelete));
				}
				addToast({
					title: translate("Sucesso"),
					description: translate("FCA excluída com sucesso"),
					color: "green"
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir FCA"), addToast);
			},
			true
		);
	};

	const executeQuery = async () => {
		try{
			setLoading(true);
			const {data} = await RCCServicesApi.getRequest({
				method: "FCA/List",
				urlParameters: {
					id,
					id2: id2 || "",
					tablesRelated
				}
			});
			const fcasFromDB = data as IFCA[];
			setFcas(fcasFromDB.length === 1 ? [{...fcasFromDB[0], expanded: true, id: disabled ? "disabled" : v4()}] : fcasFromDB.map((fca) => ({
				...fca,
				id: disabled ? "disabled" : v4()  
			})));
			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao listar FCA"), addToast);
		}
	};

	const updateFCAs = (index: number, defaultValueX: IInternalFCA, editAttachments: boolean, attachmentsNumber: number) => {
		setFcas((prevState) => prevState.map((pState, indx) => {
			if(indx !== index)
				return {
					...pState,
					expanded: false
				};

			const updateState = {
				...defaultValueX,
				id: disabled ? "disabled" : v4(),
				expanded: true,
				attachmentsNumber : pState.attachmentsNumber
			};

			if(editAttachments)
				updateState.attachmentsNumber = attachmentsNumber;

			return updateState;
		}));
	};

	useImperativeHandle(ref, () => ({
		executeQuery
	}));

	const FCAList = useMemo(() => (
		<>
			{fcas.length === 0 ? (
				<RCCAlert 
					title={translate("Atenção")}
					description={translate("Não foram encontrados dados")}	
				/>
			) : (
				<SortableContainer 
					items={fcas}
					onDragEnd={handleDragEnd}
					modifiers={[restrictToVerticalAxis]}
				>
					{fcas.map((fca, index: number) => (
						<FCAItem 
							defaultValue={fca}
							index={index}
							key={v4()}
							delete={handleDeleteFCA}
							disabled={disabled}
							isPresentation={isPresentation}
							tablesRelated={tablesRelated}
							id={id}
							id2={id2}
							updateFCAs={updateFCAs}
						/>
					))}
				</SortableContainer>
			)}
		</>
	), [fcas]);

	useEffect(() => {
		if(defaultValue)
			setFcas(defaultValue);
	},[defaultValue]);


	if(loading)
		return <Loader />;

	return (
		<RCCGrid
			container
			item
			direction="column"
		>
			{/* HEADER */}
			<RCCGrid
				container
				item
				justifyContent="space-between"
			>
				<Title
					color="purple"
				>
					{translate("Análises críticas")} (FCA)
				</Title>

				{!isPresentation && (
					<CheckPermission minimumPermissionRequired={PermissionEnum.VIEW}>
						<RCCTooltip
							text={translate("Nova análise")}
							color="purple"
							position="left"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => setFcas((prevState) => prevState && prevState !== null ? 
									[...prevState.map((pv) => ({
										...pv,
										expanded: false
									})), { ina002Id: 0, id: disabled ? "disabled" : v4(), expanded: true } as IInternalFCA] : 
									[{ ina002Id: 0, id: disabled ? "disabled" : v4(), expanded: true } as IInternalFCA])
								}
								size="md"
								disabled={disabled}
							/>
						</RCCTooltip>											
					</CheckPermission>
				)}	
				
				<hr />
			</RCCGrid>
		
			{FCAList}
			
		</RCCGrid>
	);
};

export default forwardRef(FCA);
